import React, {useEffect, useRef} from 'react';
import {Link, useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import styled, {css} from 'styled-components';
import {Player} from '@lottiefiles/react-lottie-player';

import {
    Flex,
    mobileMediaQuery,
    notWebMediaQuery,
    tabletMediaQuery,
    useIsMobile,
    useIsWeb,
} from './../../styles';
import {debugCNitoUrl} from '../../helpers';

import {ReactComponent as Chevron} from './../../assets/arrow.svg';
import {ReactComponent as Close} from './../../assets/close.svg';
import {getCurrentLang} from '../../utils/i18n';
import MenuIcons from '../../assets/menu/cnito';

const focusList = [['/', '/home'], ['/live'], ['/games'], ['/cartoons']];

const CartoonitoHeader = ({pathname}) => {
    const {t} = useTranslation();
    const showBackButton = pathname.indexOf('/cartoon/') >= 0;
    const history = useHistory();
    const isMobile = useIsMobile();

    if (pathname.indexOf('/play') === 0 || pathname.indexOf('/live/') !== -1) {
        return (
            <Flex
                position="relative"
                width="100%"
                padding="34px 53px"
                justifyContent="space-between"
                background="transparent"
            >
                <Flex margin="auto 0 auto 0">
                    <BackButton onClick={() => history.goBack()}>
                        <CloseIcon light title={t('controls.goback')} />
                        {t('controls.goback')}
                    </BackButton>
                </Flex>
            </Flex>
        );
    }

    const focused = focusList.findIndex(item => item.includes(pathname));
    return (
        <Container fixed={false}>
            <Flex margin="auto 0 auto 0">
                {showBackButton ? (
                    <BackButton onClick={() => history.goBack()}>
                        <ChevronIcon title={t('controls.goback')} />
                        {!isMobile && t('controls.goback')}
                    </BackButton>
                ) : (
                    <Logo />
                )}
            </Flex>

            <ButtonsRow>
                <CNitoMenuItem
                    to={`/home${debugCNitoUrl()}`}
                    animationData={MenuIcons[0]}
                    isFocused={focused === 0}
                    title={t('cartoonito.videos.title')}
                    lineColor="#0066FF"
                    first
                />
                <Separator focused={focused === 0 || focused === 1} />
                <CNitoMenuItem
                    to={`/live${debugCNitoUrl()}`}
                    animationData={MenuIcons[1]}
                    isFocused={focused === 1}
                    title={t('cartoonito.live.title')}
                    lineColor="#F1328A"
                />
                <Separator focused={focused === 1 || focused === 2} />
                <CNitoMenuItem
                    to={`/games${debugCNitoUrl()}`}
                    animationData={MenuIcons[2]}
                    isFocused={focused === 2}
                    title={t('cartoonito.games.title')}
                    lineColor="#32BF5E"
                />
                <Separator focused={focused === 2 || focused === 3} />
                <CNitoMenuItem
                    to={`/cartoons${debugCNitoUrl()}`}
                    animationData={MenuIcons[3]}
                    isFocused={focused === 3}
                    title={t('cartoonito.cartoons.title')}
                    lineColor="#F6D703"
                    fixAnimationSize
                    last
                />
            </ButtonsRow>

            <ParentsButton white={focused === 3 || focused === 1} />
        </Container>
    );
};

const Container = styled(Flex)`
    position: absolute;
    z-index: 100;
    top: 10px;
    box-sizing: border-box;
    transition: background-color 0.3s ease;
    height: 80px;
    flex-wrap: nowrap;
    background-color: 'transparent'};
    width: 100%;
    max-width: 1520px;
    padding: 15px 40px;
    justify-content: space-between;
    left: 0;
    right: 0;
    margin: 0 auto;

    ${mobileMediaQuery} {
        height: 100px;
        padding: 15px 25px;
        top: 0px;
    }

    ${({fixed}) =>
        fixed &&
        css`
            position: relative;
        `}
`;

/* eslint-disable max-len */
const Logo = styled.span`
    width: 235px;
    height: 52px;
    background-image: url(${require('../../assets/cnito_logo_mixed.png')});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;

    ${notWebMediaQuery} {
        width: 30px;
        height: 30px;
        background-image: url(${require('../../assets/cnito_icon.png')});
    }
`;

const ButtonsRow = styled.nav`
    height: 100%;
    width: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: white;
    height: 37px;
    border-radius: 40px;
    margin-right: 40px;

    ${tabletMediaQuery} {
        margin-right: 140px;
    }

    ${notWebMediaQuery} {
        margin-left: auto;
        margin-right: auto;
    }

    ${mobileMediaQuery} {
        border-radius: 20px;
    }

    filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.2));
`;

const BackButton = styled(Flex)`
    color: rgb(0, 0, 0);
    font-size: 22px;
    font-family: 'Rodger';
    font-weight: bold;
    letter-spacing: 1.22px;
`;

const ChevronIcon = styled(Chevron)`
    margin-right: 14px;
    width: 12px;
    height: 22px;
    transform: scaleX(-1);
    path {
        fill: ${({light}) => (light ? '#fff' : '#000')};
    }
`;

const CloseIcon = styled(Close)`
    margin-right: 14px;
    width: 28px;
    height: 28px;

    g {
        fill: ${({light}) => (light ? '#fff' : '#000')};
    }

    ${notWebMediaQuery} {
        width: 20px;
        height: 20px;
    }
`;

const Separator = styled.div`
    width: 1px;
    height: ${props => (props.focused ? '100%' : 'calc(100% - 8px)')};
    background-color: #e6e6e6;
    transition: height 0.2s ease;
`;

const MenuItemHolder = styled(Link)`
    position: relative;
    display: flex;
    flex-direction: row;
    height: 100%;
    align-self: flex-start;
    align-items: center;
    color: transparent;
    overflow: hidden;
    transition: background-color 0.2s ease;
    
    ${props => props.isFocused && 'background-color: #ededed;'}

    ${props =>
        props.first &&
        css`
            border-top-left-radius: 40px;
            border-bottom-left-radius: 40px;
        `}

    ${props =>
        props.last &&
        css`
            border-top-right-radius: 40px;
            border-bottom-right-radius: 40px;
        `}

    &:hover {
        background-color: #ededed;
    }
`;

const AnimationHolder = styled.div`
    height: 44px;
    width: ${props => (props.fixAnimationSize ? 40 : 44)}px;
    top: ${props => (props.fixAnimationSize ? -10 : -15)}px;
    left: 5px;
    position: absolute;
    pointer-events: none;

    ${mobileMediaQuery} {
        height: 38px;
        width: ${props => (props.fixAnimationSize ? 34 : 38)}px;
        left: 0px;
        right: 0px;
        margin-left: auto;
        margin-right: auto;
        top: ${props => (props.fixAnimationSize ? -12 : -17)}px;
    }
`;

const MenuText = styled.span`
    font-family: Rodger;
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 21px;
    text-align: center;
    letter-spacing: 0.0225em;

    color: #000000;
    text-transform: uppercase;
    white-space: nowrap;
    padding-left: 47px;
    padding-right: 17px;

    ${mobileMediaQuery} {
        font-size: 10px;
        font-weight: 700;
        padding-left: 12px;
        padding-right: 12px;
        margin-top: 18px;
    }
`;

const CNitoMenuItem = props => {
    const animationRef = useRef(null);

    useEffect(() => {
        if (props.isFocused && animationRef.current) {
            animationRef.current.play();
        }
    }, [animationRef, props.isFocused]);

    return (
        <MenuContainer>
            <MenuItemHolder
                to={props.to}
                first={props.first}
                last={props.last}
                isFocused={props.isFocused}
            >
                <MenuText>{props.title}</MenuText>
                <Line color={props.lineColor} isFocused={props.isFocused} />
            </MenuItemHolder>
            <AnimationHolder fixAnimationSize={props.fixAnimationSize}>
                <Player
                    ref={animationRef}
                    src={props.animationData}
                    autoplay={props.isFocused}
                />
            </AnimationHolder>
        </MenuContainer>
    );
};

const MenuContainer = styled.div`
    position: relative;
    height: 100%;
`;

const Line = styled.div`
    position: absolute;
    width: 100%;
    height: 3px;
    background-color: ${props => props.color};
    bottom: 0px;
    transition: transform 0.2s ease-in-out;
    transform-origin: left;
    transform: ${props => (props.isFocused ? 'scaleX(1)' : 'scaleX(0)')};
`;

const ParentsButton = props => {
    const {t} = useTranslation();
    const isWeb = useIsWeb();
    let link = getCurrentLang() === 'es' ? '/padres' : '/pais';

    return (
        <WrappedLink to={`${link}${debugCNitoUrl()}`}>
            <ParentImage
                src={
                    props.white && !isWeb
                        ? require('../../assets/ic_parents_white.png')
                        : require('../../assets/ic_parents.png')
                }
                alt="Parents"
            />
            {isWeb && <TitleText>{t('parentalPage.title')}</TitleText>}
        </WrappedLink>
    );
};

const WrappedLink = styled(Link)`
    color: transparent;
    display: flex;
    flex-direction: row;
    background-color: rgba(255, 255, 255, 0.5);
    border-radius: 10px;
    padding: 10px 15px;
    align-self: center;
    align-items: center;

    ${notWebMediaQuery} {
        background-color: transparent;
        padding: 0px;
    }
`;

const TitleText = styled.div`
    font-family: Rodger;
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 19px;
    letter-spacing: 0.04em;
    margin-left: 10px;
    color: black;
    text-transform: uppercase;
`;

const ParentImage = styled.img`
    width: 22px;
    height: 22px;

    ${tabletMediaQuery} {
        width: 30px;
        height: 30px;
    }

    ${mobileMediaQuery} {
        width: 24px;
        height: 24px;
    }
`;

export default CartoonitoHeader;
